import { Reveal } from "./Reveal";
import {
  TS_BADGES,
  TS_CASE_STUDY_HREF,
  TS_HEADLINE,
  TS_LOGO,
  TS_PHOTO,
  TS_STATS,
  TS_TEASER_QUOTE,
} from "../../use-cases/case-studies/teachingStrategies";

/**
 * Confetti in the campaign's badge colors, kept to the card's edges so it
 * never sits on the content: [x%, y%, size px, color, shape].
 */
const CONFETTI: [number, number, number, string, "dot" | "bar"][] = [
  // Top edge
  [30, 4, 6, "#fdb71f", "dot"],
  [38, 2, 9, "#ff4d2e", "bar"],
  [46, 3, 12, "#8e67ab", "bar"],
  [55, 6, 7, "#039dbf", "dot"],
  [64, 2, 8, "#fdb71f", "bar"],
  [72, 4, 7, "#039dbf", "bar"],
  [81, 7, 6, "#ff4d2e", "dot"],
  [88, 3, 10, "#8e67ab", "bar"],
  [96, 8, 10, "#fdb71f", "dot"],
  // Left edge
  [1.5, 22, 7, "#8e67ab", "dot"],
  [2, 40, 8, "#039dbf", "dot"],
  [1, 62, 8, "#fdb71f", "bar"],
  [2.5, 82, 6, "#ff4d2e", "dot"],
  // Right edge
  [98, 30, 7, "#039dbf", "dot"],
  [97.5, 52, 9, "#ff4d2e", "bar"],
  [98.5, 74, 6, "#fdb71f", "dot"],
  // Bottom edge
  [12, 95, 7, "#039dbf", "bar"],
  [24, 97, 6, "#8e67ab", "dot"],
  [42, 95, 9, "#fdb71f", "bar"],
  [60, 96, 8, "#ff4d2e", "dot"],
  [76, 97, 7, "#039dbf", "dot"],
  [90, 95, 10, "#8e67ab", "bar"],
];

/**
 * Home-page teaser for the Teaching Strategies case study: a few highlights
 * in a "sticker" card that links through to the full version on the
 * Education page. The card is a little more playful than the rest of the
 * site on purpose, but keeps its type and button styles.
 */
export const CaseStudyTeaser = () => (
  <section
    aria-labelledby="case-study-teaser-heading"
    className="bg-fwd-white px-4 sm:px-6 md:px-16 lg:px-20 pt-8 pb-20 md:pt-12 md:pb-28"
  >
    <Reveal className="max-w-5xl mx-auto">
      <div className="relative -rotate-[0.5deg] motion-safe:hover:rotate-0 motion-safe:transition-transform motion-safe:duration-300 rounded-2xl border-2 border-fwd-ink bg-[#fff4d6] shadow-[6px_6px_0_#111118] px-5 pt-14 pb-8 sm:px-10 md:px-14 md:pt-16 md:pb-12">
        {/* Sticker tab */}
        <span className="absolute z-10 -top-3.5 left-6 md:left-12 -rotate-2 whitespace-nowrap rounded-full bg-fwd-red text-white text-[0.65rem] md:text-xs font-semibold tracking-widest uppercase px-4 py-1.5 shadow-[3px_3px_0_#111118]">
          Case study
        </span>

        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[14px]"
        >
          {/* The campaign photo, fading in from the cream: behind the right
              column on wide screens, behind the bottom on phones. */}
          <img
            src={TS_PHOTO.src}
            alt=""
            width={TS_PHOTO.width}
            height={TS_PHOTO.height}
            loading="lazy"
            decoding="async"
            className="absolute bottom-0 right-0 w-full h-[42%] md:h-full md:w-[58%] object-cover object-[60%_20%]"
          />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,#fff4d6_60%,rgba(255,244,214,0.4)_78%,rgba(255,244,214,0.1))] md:bg-[linear-gradient(to_right,#fff4d6_48%,rgba(255,244,214,0.6)_64%,rgba(255,244,214,0.05)_92%)]" />
          {CONFETTI.map(([x, y, size, color, shape], i) => (
            <span
              key={i}
              className="absolute"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: shape === "bar" ? size * 2.4 : size,
                height: size,
                background: color,
                borderRadius: size,
                transform: `rotate(${(i * 37) % 90}deg)`,
                opacity: 0.7,
              }}
            />
          ))}
        </div>

        <div className="relative grid gap-10 md:grid-cols-[1fr_auto] md:items-center">
          <div className="min-w-0">
            <img
              src={TS_LOGO.src}
              alt={TS_LOGO.alt}
              width={TS_LOGO.width}
              height={TS_LOGO.height}
              loading="lazy"
              className="w-40 md:w-52 max-w-full h-auto mb-6"
            />
            <h2
              id="case-study-teaser-heading"
              className="font-display text-[clamp(1.8rem,3.4vw,2.8rem)] leading-tight text-fwd-ink mb-8 max-w-xl"
            >
              {TS_HEADLINE}
            </h2>
            <dl className="grid grid-cols-[repeat(auto-fill,minmax(max(8rem,40%),1fr))] gap-6 md:gap-10 max-w-xl mb-9">
              {TS_STATS.map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col-reverse justify-end"
                >
                  <dt className="text-fwd-ink/70 text-sm mt-1.5 leading-snug">
                    {s.label}
                  </dt>
                  <dd className="font-display text-fwd-ink text-[clamp(2.2rem,4.5vw,3.2rem)] leading-none">
                    {s.value}
                  </dd>
                </div>
              ))}
            </dl>
            <a
              href={TS_CASE_STUDY_HREF}
              className="inline-block bg-fwd-red text-white text-sm font-semibold tracking-wider uppercase px-7 py-3.5 rounded hover:opacity-90 transition-opacity focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-fwd-ink after:absolute after:inset-0 after:content-['']"
            >
              Read the case study <span aria-hidden="true">&rarr;</span>
            </a>
          </div>

          {/* Badges + a recipient's reaction */}
          <div className="relative flex flex-col items-start md:items-center md:self-end gap-5 md:gap-6 md:w-80">
            <div className="relative shrink-0 rounded-2xl rounded-bl-none border-2 border-fwd-ink bg-white px-5 py-4 font-display text-xl md:text-2xl leading-snug text-fwd-ink shadow-[4px_4px_0_#111118] rotate-1 max-w-[17rem] md:max-w-[19rem]">
              &ldquo;{TS_TEASER_QUOTE}&rdquo;
            </div>
            <div className="flex -space-x-3 md:-space-x-5 order-first md:order-none">
              {TS_BADGES.map((b, i) => (
                <img
                  key={b.src}
                  src={b.src}
                  alt={`${b.label} badge`}
                  width={112}
                  height={112}
                  loading="lazy"
                  className="w-[min(4rem,16vw)] h-[min(4rem,16vw)] md:w-28 md:h-28 rounded-full bg-white border-2 border-fwd-ink shadow-[0_6px_18px_rgba(17,17,24,0.25)]"
                  style={{ transform: `rotate(${(i - 1) * 6}deg)` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);
